import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { LoadingState, ErrorState } from '@/components/ui/States';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import MobileUploadQRModal, { type MobileSessionStatus, type MobileUploadedDoc } from '@/components/MobileUploadQRModal';
import { useAuth } from '@/hooks/useAuth';
import {
  useOutsourcedItem, useReferenceLabs, useCollectSample, useDispatchOutsourcedSample, useChangeSampleReferenceLab,
  useUpdateOutsourcedStatus, useAttachOutsourcedReport, useRunOutsourcedOcr, useSaveOutsourcedResult,
  useFinalizeOutsourced, useVerifyResult, useReopenResult,
} from './hooks';
import { openOutsourcedReport } from './api';
import { AbnormalFlag, Field, LabStatusBadge, SectionTitle, fmtDate, fmtDateTime } from './ui';
import type { LabFlag, OcrResult, OutsourcedDetail, TranscriptionInput } from './types';

/**
 * One outside-laboratory test, end to end.
 *
 * Every button here mirrors a server rule and is a convenience only — the
 * transition table, the grants and the patient check are all re-applied
 * server-side (routes/laboratory/outsourced.js). What this screen adds that the
 * server cannot is the thing a person must SEE before attaching or finalizing:
 * who the patient is.
 */
export interface OutsideLabItemDialogProps {
  orderItemId: number | null;
  onClose: () => void;
}

const has = (modules: string[] | undefined, m: string) => !!modules?.includes(m);
const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));

export default function OutsideLabItemDialog({ orderItemId, onClose }: OutsideLabItemDialogProps) {
  const { user } = useAuth();
  const { data: item, isLoading, error, refetch } = useOutsourcedItem(orderItemId);

  return (
    <Dialog open={orderItemId !== null} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl">
        {isLoading && <LoadingState label="Loading…" />}
        {error && <ErrorState message={errMsg(error)} onRetry={() => refetch()} />}
        {item && <ItemBody item={item} modules={user?.modules} />}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ItemBody({ item, modules }: { item: OutsourcedDetail; modules: string[] | undefined }) {
  const [actionError, setActionError] = useState<string | null>(null);
  const run = async (fn: () => Promise<unknown>) => {
    setActionError(null);
    try { await fn(); } catch (e) { setActionError(errMsg(e)); }
  };

  const canOutsource = has(modules, 'lab:outsource');
  const canCollect = has(modules, 'lab:collect');
  const canEnter = has(modules, 'lab:result-entry');
  const canVerify = has(modules, 'lab:verify');
  const canRelease = has(modules, 'lab:release');
  const canAmend = has(modules, 'lab:amend');

  const collect = useCollectSample();
  const verify = useVerifyResult();
  const finalize = useFinalizeOutsourced();
  const reopen = useReopenResult();
  const status = useUpdateOutsourcedStatus();

  const [sendBackOpen, setSendBackOpen] = useState(false);
  const [sendBackReason, setSendBackReason] = useState('');
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState('');

  const ids = { orderItemId: item.order_item_id, orderId: item.order_id, uhid: item.uhid };
  const current = item.reports.find((r) => r.status === 'current') ?? null;
  const pendingReport = ['SENT_TO_REFERENCE_LAB', 'AWAITING_REPORT', 'REPORT_RECEIVED'].includes(item.status);
  const transcribable = ['REPORT_UPLOADED', 'RESULT_ENTERED'].includes(item.status);

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex flex-wrap items-center gap-2">
          <span>{item.test_name}</span>
          <LabStatusBadge status={item.status} processingType="outsourced" />
          {item.overdue && <Badge variant="danger">Report overdue</Badge>}
          {item.has_critical && <Badge variant="danger">Critical</Badge>}
        </DialogTitle>
      </DialogHeader>

      <div className="max-h-[72vh] space-y-5 overflow-y-auto pr-1">
        <IdentityBlock item={item} />

        <section>
          <SectionTitle>Progress</SectionTitle>
          <div className="grid grid-cols-2 gap-3 rounded-xl border border-border p-3 sm:grid-cols-4">
            <Field label="Ordered">{fmtDateTime(item.ordered_at)}{item.ordering_doctor_name ? ` · ${item.ordering_doctor_name}` : ''}</Field>
            <Field label="Sample collected">{item.collected_at ? `${fmtDateTime(item.collected_at)} · ${item.collected_by ?? ''}` : '—'}</Field>
            <Field label="Sent to reference lab">{item.sent_at ? `${fmtDateTime(item.sent_at)} · ${item.sent_by ?? ''}` : '—'}</Field>
            <Field label="External accession no."><span className="font-mono">{item.external_accession_number ?? '—'}</span></Field>
            <Field label="Expected report">
              <span className={item.overdue ? 'font-semibold text-danger-ink' : undefined}>{fmtDate(item.expected_report_at)}</span>
            </Field>
            <Field label="Report received">{item.external_report_received_at ? `${fmtDateTime(item.external_report_received_at)} · ${item.external_report_received_by ?? ''}` : '—'}</Field>
            <Field label="Verified">{item.verified_at ? `${fmtDateTime(item.verified_at)} · ${item.verified_by ?? ''}` : '—'}</Field>
            <Field label="Finalized">{item.finalized_at ? `${fmtDateTime(item.finalized_at)} · ${item.finalized_by ?? ''}` : '—'}</Field>
          </div>
          {item.dispatch_notes && <p className="mt-1 text-[12px] text-muted-foreground">Dispatch notes: {item.dispatch_notes}</p>}
          {item.sent_back_reason && item.status === 'REPORT_UPLOADED' && (
            <div className="mt-2 rounded-lg bg-warn-soft px-3 py-2 text-[12px] text-caution-ink">
              Sent back for re-entry: {item.sent_back_reason}
            </div>
          )}
        </section>

        {/* ── Collection & dispatch ─────────────────────────────── */}
        {(item.status === 'PENDING_COLLECTION' || item.status === 'COLLECTED' || pendingReport) && (
          <section className="space-y-2">
            <SectionTitle>Sample</SectionTitle>
            <div className="flex flex-wrap gap-2">
              {item.status === 'PENDING_COLLECTION' && canCollect && item.sample_id && (
                <Button size="sm" disabled={collect.isPending}
                  onClick={() => run(() => collect.mutateAsync({ id: item.sample_id as number, orderId: item.order_id }))}>
                  Record collection
                </Button>
              )}
              {(item.status === 'PENDING_COLLECTION' || item.status === 'COLLECTED') && canOutsource && item.sample_id && (
                <ChangeLabButton item={item} onError={setActionError} />
              )}
              {item.status === 'COLLECTED' && canOutsource && item.sample_id && (
                <DispatchButton sampleId={item.sample_id} referenceLabName={item.reference_lab_name}
                  orderItemId={item.order_item_id} orderId={item.order_id} onError={setActionError} />
              )}
              {pendingReport && canOutsource && (
                <TrackingControls item={item} busy={status.isPending}
                  onSubmit={(body) => run(() => status.mutateAsync({ orderItemId: item.order_item_id, orderId: item.order_id, body }))} />
              )}
            </div>
          </section>
        )}

        {/* ── Report ────────────────────────────────────────────── */}
        <section className="space-y-2">
          <SectionTitle>Reference laboratory report</SectionTitle>
          {item.reports.length === 0 && (
            <p className="text-[12px] text-muted-foreground">
              {pendingReport ? 'No report attached yet.' : 'A report can be attached once the sample has been sent to the reference laboratory.'}
            </p>
          )}
          {item.reports.length > 0 && (
            <div className="divide-y divide-border rounded-xl border border-border">
              {item.reports.map((r) => (
                <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-[12px]">
                  <div>
                    <span className="font-medium">{r.original_name}</span>{' '}
                    <Badge variant={r.status === 'current' ? 'success' : 'neutral'}>{r.status === 'current' ? 'Current' : 'Superseded'}</Badge>
                    {r.upload_channel === 'mobile_qr' && <Badge variant="info" className="ml-1">Scanned by phone</Badge>}
                    <div className="text-muted-foreground">
                      Uploaded {fmtDateTime(r.uploaded_at)} by {r.uploaded_by ?? '—'}
                      {r.report_date ? ` · report date ${fmtDate(r.report_date)}` : ''}
                      {r.reference_lab_name ? ` · ${r.reference_lab_name}` : ''}
                    </div>
                    {r.replacement_reason && <div className="text-muted-foreground">Replaced previous report: {r.replacement_reason}</div>}
                    {r.superseded_at && <div className="text-muted-foreground">Superseded {fmtDateTime(r.superseded_at)} by {r.superseded_by ?? '—'}</div>}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => run(() => openOutsourcedReport(r.id))}>View report</Button>
                </div>
              ))}
            </div>
          )}
          {canOutsource && (pendingReport || transcribable) && (
            <UploadPanel item={item} replacing={!!current} />
          )}
          {!canOutsource && pendingReport && (
            <p className="text-[11px] text-muted-foreground">Attaching reports needs the “Send out samples & attach outside reports” permission.</p>
          )}
        </section>

        {/* ── Result ────────────────────────────────────────────── */}
        <section className="space-y-2">
          <SectionTitle>Result</SectionTitle>
          {item.result_mode === 'report_only' && item.values.length === 0 && item.result_id && (
            <p className="text-[12px] text-muted-foreground">Report only — the attached original report is the result; no values were transcribed.</p>
          )}
          {item.values.length > 0 && !(transcribable && canEnter) && <ValuesTable item={item} />}
          {transcribable && canEnter && current && <TranscriptionPanel item={item} />}
          {!item.result_id && !transcribable && <p className="text-[12px] text-muted-foreground">No result yet.</p>}

          <div className="flex flex-wrap gap-2">
            {item.status === 'RESULT_ENTERED' && canVerify && (
              <>
                <Button variant="success" size="sm" disabled={verify.isPending}
                  onClick={() => run(() => verify.mutateAsync({ orderItemId: item.order_item_id, orderId: item.order_id, decision: 'verified' }))}>
                  Verify
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { setSendBackOpen(true); setSendBackReason(''); }}>Send back</Button>
              </>
            )}
            {item.status === 'VERIFIED' && canRelease && (
              <Button size="sm" disabled={finalize.isPending} onClick={() => run(() => finalize.mutateAsync(ids))}>
                Finalize — publish to patient record
              </Button>
            )}
            {(item.status === 'VERIFIED' || item.status === 'REPORTED') && canAmend && (
              <Button variant="ghost" size="sm" onClick={() => { setReopenOpen(true); setReopenReason(''); }}>Reopen for correction</Button>
            )}
          </div>
          {item.status === 'RESULT_ENTERED' && !canVerify && (
            <p className="text-[11px] text-muted-foreground">Waiting for an authorized verifier (lab:verify).</p>
          )}
        </section>

        {actionError && <ErrorState message={actionError} />}

        <Timeline item={item} />
      </div>

      <Dialog open={sendBackOpen} onOpenChange={setSendBackOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Send back for re-entry</DialogTitle></DialogHeader>
          <Textarea rows={3} value={sendBackReason} onChange={(e) => setSendBackReason(e.target.value)}
            placeholder="What is wrong with the transcription? (required)" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSendBackOpen(false)}>Cancel</Button>
            <Button disabled={!sendBackReason.trim() || verify.isPending}
              onClick={() => run(async () => {
                await verify.mutateAsync({ orderItemId: item.order_item_id, orderId: item.order_id, decision: 'sent_back', comments: sendBackReason.trim() });
                setSendBackOpen(false);
              })}>
              Send back
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reopenOpen} onOpenChange={setReopenOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Reopen for correction</DialogTitle></DialogHeader>
          <p className="text-[12px] text-muted-foreground">
            The test returns to “report uploaded”. The correction (a replaced report and/or a re-transcribed result) must be
            verified and finalized again. The original report, the earlier result and this reason all stay on the audit trail.
          </p>
          <Textarea rows={3} value={reopenReason} onChange={(e) => setReopenReason(e.target.value)}
            placeholder="Why is this being corrected? (required)" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReopenOpen(false)}>Cancel</Button>
            <Button variant="danger" disabled={!reopenReason.trim() || reopen.isPending}
              onClick={() => run(async () => {
                await reopen.mutateAsync({ orderItemId: item.order_item_id, orderId: item.order_id, reason: reopenReason.trim() });
                setReopenOpen(false);
              })}>
              Reopen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Who this is — shown before anything is attached, verified or finalized,
 * because the most expensive mistake on this screen is the right report on the
 * wrong patient.
 */
function IdentityBlock({ item }: { item: OutsourcedDetail }) {
  return (
    <section className="rounded-xl border-2 border-teal/40 bg-muted/30 p-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Field label="Patient name"><span className="font-semibold">{item.patient_name}</span></Field>
        <Field label="MRN / UHID"><span className="font-mono font-semibold">{item.uhid}</span></Field>
        <Field label="DOB / Age">
          {item.dob ? fmtDate(item.dob) : '—'}{item.age !== null && item.age !== undefined ? ` · ${item.age}y` : ''}{item.gender ? ` · ${item.gender}` : ''}
        </Field>
        <Field label="Lab order ID"><span className="font-mono">{item.order_number}</span></Field>
        <Field label="Test">{item.test_name} <span className="font-mono text-[11px] text-muted-foreground">({item.test_code})</span></Field>
        <Field label="Reference laboratory">{item.reference_lab_name ?? '—'}</Field>
      </div>
    </section>
  );
}

function ChangeLabButton({ item, onError }: { item: OutsourcedDetail; onError: (m: string | null) => void }) {
  const labs = useReferenceLabs();
  const change = useChangeSampleReferenceLab();
  const [open, setOpen] = useState(false);
  const [labId, setLabId] = useState<number | ''>('');
  const [reason, setReason] = useState('');
  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => { setOpen(true); setLabId(''); setReason(''); }}>Change reference lab</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Change reference laboratory</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <p className="text-[12px] text-muted-foreground">Currently routed to {item.reference_lab_name}. Every test on this sample moves together.</p>
            <select className="h-9 w-full rounded-lg border border-border bg-card px-2 text-[13px]" value={labId}
              onChange={(e) => setLabId(e.target.value ? Number(e.target.value) : '')}>
              <option value="">Choose…</option>
              {(labs.data ?? []).filter((l) => l.id !== item.reference_lab_id).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required, audited)" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button disabled={!labId || !reason.trim() || change.isPending}
              onClick={async () => {
                onError(null);
                try {
                  await change.mutateAsync({ sampleId: item.sample_id as number, orderItemId: item.order_item_id, orderId: item.order_id, reference_lab_id: labId as number, reason: reason.trim() });
                  setOpen(false);
                } catch (e) { onError(errMsg(e)); }
              }}>
              Change
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Shared by this dialog and the Samples tab (SampleActions) — one dispatch form. */
export function DispatchButton({ sampleId, referenceLabName, orderItemId, orderId, onError }: {
  sampleId: number; referenceLabName: string | null | undefined; orderItemId?: number; orderId?: number;
  onError: (m: string | null) => void;
}) {
  const dispatch = useDispatchOutsourcedSample();
  const [open, setOpen] = useState(false);
  const [accession, setAccession] = useState('');
  const [expected, setExpected] = useState('');
  const [notes, setNotes] = useState('');
  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)}>Send to {referenceLabName ?? 'reference lab'}</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Send sample to {referenceLabName ?? 'the reference laboratory'}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <label className="block text-[12px]">
              <span className="mb-1 block text-muted-foreground">External accession / lab order number (if already issued)</span>
              <Input value={accession} onChange={(e) => setAccession(e.target.value)} placeholder="e.g. MET-2026-001234" />
            </label>
            <label className="block text-[12px]">
              <span className="mb-1 block text-muted-foreground">Expected report date (defaults to the test's turnaround)</span>
              <Input type="date" value={expected} onChange={(e) => setExpected(e.target.value)} />
            </label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Courier, pickup reference… (optional)" />
            <p className="text-[11px] text-muted-foreground">
              With an accession number the test moves straight to “Awaiting report”; without one it is “Sent to reference lab” until it is recorded.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button disabled={dispatch.isPending}
              onClick={async () => {
                onError(null);
                try {
                  await dispatch.mutateAsync({
                    sampleId, orderItemId, orderId,
                    body: {
                      external_accession_number: accession.trim() || undefined,
                      expected_report_at: expected || undefined,
                      notes: notes.trim() || undefined,
                    },
                  });
                  setOpen(false);
                } catch (e) { onError(errMsg(e)); }
              }}>
              {dispatch.isPending ? 'Recording…' : 'Confirm sent'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function TrackingControls({ item, busy, onSubmit }: {
  item: OutsourcedDetail; busy: boolean;
  onSubmit: (body: { status?: 'AWAITING_REPORT' | 'REPORT_RECEIVED'; external_accession_number?: string; expected_report_at?: string }) => void;
}) {
  const [accession, setAccession] = useState(item.external_accession_number ?? '');
  const [expected, setExpected] = useState('');
  return (
    <div className="flex w-full flex-wrap items-end gap-2 rounded-xl border border-border p-2">
      <label className="text-[12px]">
        <span className="mb-1 block text-muted-foreground">Accession no.</span>
        <Input className="w-[200px]" value={accession} onChange={(e) => setAccession(e.target.value)} />
      </label>
      <label className="text-[12px]">
        <span className="mb-1 block text-muted-foreground">Expected report</span>
        <Input type="date" className="w-[150px]" value={expected} onChange={(e) => setExpected(e.target.value)} />
      </label>
      <Button variant="ghost" size="sm" disabled={busy || (!accession.trim() && !expected) || (accession.trim() === (item.external_accession_number ?? '') && !expected)}
        onClick={() => onSubmit({ external_accession_number: accession.trim() || undefined, expected_report_at: expected || undefined })}>
        Save tracking
      </Button>
      {item.status === 'SENT_TO_REFERENCE_LAB' && (
        <Button variant="ghost" size="sm" disabled={busy || !accession.trim()}
          onClick={() => onSubmit({ status: 'AWAITING_REPORT', external_accession_number: accession.trim() })}>
          Mark awaiting report
        </Button>
      )}
      {item.status !== 'REPORT_RECEIVED' && (
        <Button variant="ghost" size="sm" disabled={busy} onClick={() => onSubmit({ status: 'REPORT_RECEIVED' })}>
          Mark report received
        </Button>
      )}
    </div>
  );
}

/**
 * Attach (or replace) the report. Two front doors, one server check: a file
 * picked here, or the EXISTING QR "scan to upload" flow (the same modal the
 * Documents tab uses) whose arrival is then linked. Either way the person must
 * confirm the patient by typing the UHID, and the server compares it.
 */
function UploadPanel({ item, replacing }: { item: OutsourcedDetail; replacing: boolean }) {
  const attach = useAttachOutsourcedReport();
  const [file, setFile] = useState<File | null>(null);
  const [scanned, setScanned] = useState<MobileUploadedDoc | null>(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [confirmUhid, setConfirmUhid] = useState('');
  const [reportDate, setReportDate] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(!replacing);

  const confirmed = confirmUhid.trim().toLowerCase() === item.uhid.toLowerCase();
  const source = file ? 'file' : scanned ? 'scan' : null;

  if (!open) {
    return <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>Replace report…</Button>;
  }

  const submit = async () => {
    setError(null);
    try {
      await attach.mutateAsync({
        orderItemId: item.order_item_id, orderId: item.order_id, uhid: item.uhid,
        input: {
          file: file ?? undefined,
          document_id: file ? undefined : scanned?.id,
          confirmed_uhid: confirmUhid.trim(),
          report_date: reportDate || undefined,
          replace: replacing,
          replacement_reason: replacing ? reason.trim() : undefined,
        },
      });
      setFile(null); setScanned(null); setConfirmUhid(''); setReason(''); setReportDate('');
      if (replacing) setOpen(false);
    } catch (e) { setError(errMsg(e)); }
  };

  return (
    <div className="space-y-2 rounded-xl border border-border p-3">
      <div className="text-[13px] font-semibold">{replacing ? 'Replace the attached report' : 'Attach the reference laboratory report'}</div>
      <div className="flex flex-wrap items-center gap-2">
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-[12px] hover:bg-muted">
          Choose file (PDF, JPG, PNG)
          <input type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" className="hidden"
            onChange={(e) => { setFile(e.target.files?.[0] ?? null); setScanned(null); }} />
        </label>
        <Button variant="ghost" size="sm" onClick={() => setQrOpen(true)}>📷 Scan with phone</Button>
        {source === 'file' && <span className="text-[12px]">{file?.name}</span>}
        {source === 'scan' && <span className="text-[12px]">Scanned: {scanned?.original_name}</span>}
      </div>

      {qrOpen && (
        <MobileUploadQRModal
          uhid={item.uhid}
          category="Lab Report"
          title={`Scan ${item.test_name} report`}
          hint={`Photograph or upload the ${item.reference_lab_name ?? 'reference laboratory'} report for ${item.patient_name} (${item.uhid}). It is linked to this test only after you confirm the patient below.`}
          onClose={() => setQrOpen(false)}
          onReceived={(s: MobileSessionStatus) => {
            const newest = s.documents[0];
            if (newest) { setScanned(newest); setFile(null); }
          }}
        />
      )}

      <div className="grid gap-2 sm:grid-cols-3">
        <label className="text-[12px]">
          <span className="mb-1 block text-muted-foreground">Confirm patient — type the UHID of {item.patient_name}</span>
          <Input value={confirmUhid} onChange={(e) => setConfirmUhid(e.target.value)} placeholder={item.uhid.replace(/./g, '•')}
            invalid={!!confirmUhid && !confirmed} />
        </label>
        <label className="text-[12px]">
          <span className="mb-1 block text-muted-foreground">Report date (as printed)</span>
          <Input type="date" value={reportDate} onChange={(e) => setReportDate(e.target.value)} />
        </label>
        {replacing && (
          <label className="text-[12px]">
            <span className="mb-1 block text-muted-foreground">Reason for replacement (required)</span>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Corrected report issued" />
          </label>
        )}
      </div>
      {confirmUhid && !confirmed && (
        <p className="text-[12px] font-semibold text-danger-ink">That UHID is not this order's patient. Check the report before attaching it.</p>
      )}
      {error && <ErrorState message={error} />}
      <div className="flex gap-2">
        <Button size="sm" disabled={!source || !confirmed || (replacing && !reason.trim()) || attach.isPending} onClick={submit}>
          {attach.isPending ? 'Attaching…' : replacing ? 'Replace report' : 'Attach report'}
        </Button>
        {replacing && <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancel</Button>}
      </div>
    </div>
  );
}

function ValuesTable({ item }: { item: OutsourcedDetail }) {
  return (
    <div className="rounded-xl border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Parameter</TableHead><TableHead>Result</TableHead><TableHead>Unit</TableHead>
            <TableHead>Reference (as reported)</TableHead><TableHead>Flag</TableHead><TableHead>Remarks</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {item.values.map((v) => (
            <TableRow key={v.id}>
              <TableCell>{v.parameter_name}{v.entry_source === 'ocr' && <Badge variant="info" className="ml-1">OCR-assisted</Badge>}</TableCell>
              <TableCell className="font-mono font-semibold">{v.value_text}</TableCell>
              <TableCell>{v.unit ?? ''}</TableCell>
              <TableCell>{v.reference_display ?? '—'}</TableCell>
              <TableCell><AbnormalFlag flag={v.flag} /></TableCell>
              <TableCell className="text-[12px] text-muted-foreground">{v.comments ?? ''}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

interface DraftRow {
  parameter_id: number | null; parameter_name: string; value: string; unit: string;
  reference_display: string; flag: LabFlag | ''; comments: string; entry_source: 'manual' | 'ocr';
}

const FLAG_OPTIONS: Array<{ value: LabFlag | ''; label: string }> = [
  { value: '', label: 'Auto (from range)' },
  { value: 'NORMAL', label: 'Normal' }, { value: 'LOW', label: 'Low' }, { value: 'HIGH', label: 'High' },
  { value: 'CRITICAL_LOW', label: 'Critical low' }, { value: 'CRITICAL_HIGH', label: 'Critical high' },
  { value: 'ABNORMAL', label: 'Abnormal' },
];

const blankRow = (item: OutsourcedDetail): DraftRow => ({
  parameter_id: null, parameter_name: item.parameters.length === 1 ? (item.parameters[0]?.name ?? '') : item.parameters.length ? '' : item.test_name,
  value: '', unit: item.parameters[0]?.unit ?? '', reference_display: '', flag: '', comments: '', entry_source: 'manual',
});

/**
 * Transcription — Option A (report only) or Option B (structured values).
 *
 * OCR only ever FILLS THIS FORM. Nothing is saved until a person presses
 * Save, and nothing is final until someone with lab:verify verifies it.
 */
function TranscriptionPanel({ item }: { item: OutsourcedDetail }) {
  const save = useSaveOutsourcedResult();
  const ocr = useRunOutsourcedOcr();
  const [mode, setMode] = useState<'structured' | 'report_only'>(item.result_mode ?? 'structured');
  const [rows, setRows] = useState<DraftRow[]>(() => item.values.length
    ? item.values.map((v) => ({
        parameter_id: v.parameter_id, parameter_name: v.parameter_name, value: v.value_text ?? '', unit: v.unit ?? '',
        reference_display: v.reference_display ?? '', flag: v.flag === 'NOT_EVALUATED' ? '' : v.flag, comments: v.comments ?? '',
        entry_source: v.entry_source,
      }))
    : [blankRow(item)]);
  const [ocrResult, setOcrResult] = useState<OcrResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const setRow = (i: number, patch: Partial<DraftRow>) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch, entry_source: patch.entry_source ?? (patch.value !== undefined ? 'manual' : r.entry_source) } : r)));

  const runOcr = async () => {
    setError(null);
    try { setOcrResult(await ocr.mutateAsync({ orderItemId: item.order_item_id })); }
    catch (e) { setError(errMsg(e)); }
  };

  const useOcrRows = () => {
    if (!ocrResult?.rows.length) return;
    setMode('structured');
    setRows(ocrResult.rows.map((r) => ({
      parameter_id: r.parameter_id, parameter_name: r.parameter_name, value: r.value, unit: r.unit ?? '',
      reference_display: r.reference_display ?? '', flag: r.flag_source === 'printed' ? r.flag : '', comments: r.remarks ?? '',
      entry_source: 'ocr',
    })));
  };

  const submit = async () => {
    setError(null);
    const input: TranscriptionInput = mode === 'report_only'
      ? { mode }
      : {
          mode,
          values: rows.filter((r) => r.value.trim()).map((r) => ({
            parameter_id: r.parameter_id, parameter_name: r.parameter_name.trim(), value: r.value.trim(),
            unit: r.unit.trim() || null, reference_display: r.reference_display.trim() || null,
            flag: r.flag || null, comments: r.comments.trim() || null, entry_source: r.entry_source,
          })),
        };
    try { await save.mutateAsync({ orderItemId: item.order_item_id, orderId: item.order_id, uhid: item.uhid, input }); }
    catch (e) { setError(errMsg(e)); }
  };

  return (
    <div className="space-y-2 rounded-xl border border-border p-3">
      <div className="flex flex-wrap items-center gap-3 text-[13px]">
        <label className="flex items-center gap-1.5">
          <input type="radio" checked={mode === 'structured'} onChange={() => setMode('structured')} /> Enter structured result
        </label>
        <label className="flex items-center gap-1.5">
          <input type="radio" checked={mode === 'report_only'} onChange={() => setMode('report_only')} /> Report only (no values)
        </label>
        <Button variant="ghost" size="sm" className="ml-auto" disabled={ocr.isPending} onClick={runOcr}>
          {ocr.isPending ? 'Reading report…' : '🔍 Read report with OCR'}
        </Button>
      </div>

      {ocrResult && (
        <div className="space-y-1.5 rounded-lg bg-muted/40 p-2 text-[12px]">
          <div className="font-semibold">
            OCR {ocrResult.confident ? 'found a result — review it against the report before saving' : 'could not confidently extract a result — enter it manually'}
          </div>
          {ocrResult.warnings.map((w) => (
            <div key={w.code} className={w.code.includes('patient') ? 'font-semibold text-danger-ink' : 'text-caution-ink'}>⚠ {w.message}</div>
          ))}
          {ocrResult.rows.length > 0 && (
            <>
              <div className="divide-y divide-border rounded border border-border bg-card">
                {ocrResult.rows.map((r, i) => (
                  <div key={i} className="flex flex-wrap items-center justify-between gap-2 px-2 py-1">
                    <span>
                      <span className="font-medium">{r.parameter_name}</span>{' '}
                      <span className="font-mono">{r.value}</span> {r.unit ?? ''} {r.reference_display ? `(${r.reference_display})` : ''}{' '}
                      <AbnormalFlag flag={r.flag} compact />
                    </span>
                    <span className="text-muted-foreground" title={r.source_line}>
                      {r.confidence >= 0.8 ? 'high' : r.confidence >= 0.6 ? 'medium' : 'low'} confidence · “{r.source_line.slice(0, 50)}”
                    </span>
                  </div>
                ))}
              </div>
              <Button size="sm" variant="ghost" onClick={useOcrRows}>Copy into the form for review</Button>
            </>
          )}
        </div>
      )}

      {mode === 'structured' && (
        <>
          <datalist id={`params-${item.order_item_id}`}>
            {item.parameters.map((p) => <option key={p.id} value={p.name} />)}
          </datalist>
          <div className="space-y-1.5">
            {rows.map((r, i) => (
              <div key={i} className="grid grid-cols-2 gap-1.5 sm:grid-cols-[2fr_1fr_1fr_1.3fr_1.2fr_1.5fr_auto]">
                <Input list={`params-${item.order_item_id}`} placeholder="Parameter" value={r.parameter_name}
                  onChange={(e) => {
                    const match = item.parameters.find((p) => p.name === e.target.value);
                    setRow(i, { parameter_name: e.target.value, parameter_id: match?.id ?? null, unit: match?.unit ?? r.unit });
                  }} />
                <Input placeholder="Result" value={r.value} onChange={(e) => setRow(i, { value: e.target.value })} />
                <Input placeholder="Unit" value={r.unit} onChange={(e) => setRow(i, { unit: e.target.value })} />
                <Input placeholder="Ref. range, e.g. 30 - 100" value={r.reference_display} onChange={(e) => setRow(i, { reference_display: e.target.value })} />
                <select className="h-9 rounded-lg border border-border bg-card px-2 text-[12px]" value={r.flag}
                  onChange={(e) => setRow(i, { flag: e.target.value as LabFlag | '' })}>
                  {FLAG_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
                <Input placeholder="Remarks" value={r.comments} onChange={(e) => setRow(i, { comments: e.target.value })} />
                <div className="flex items-center gap-1">
                  {r.entry_source === 'ocr' && <Badge variant="info">OCR</Badge>}
                  <Button variant="ghost" size="sm" disabled={rows.length === 1} onClick={() => setRows((rs) => rs.filter((_, idx) => idx !== i))}>✕</Button>
                </div>
              </div>
            ))}
          </div>
          <Button variant="ghost" size="sm" onClick={() => setRows((rs) => [...rs, blankRow(item)])}>+ Add parameter</Button>
        </>
      )}

      {error && <ErrorState message={error} />}
      <div className="flex items-center gap-2">
        <Button size="sm" disabled={save.isPending} onClick={submit}>
          {save.isPending ? 'Saving…' : item.status === 'RESULT_ENTERED' ? 'Save changes' : 'Save — send for verification'}
        </Button>
        <span className="text-[11px] text-muted-foreground">Saved results are not final until verified by an authorized verifier.</span>
      </div>
    </div>
  );
}

const ACTION_LABELS: Record<string, string> = {
  collect: 'Sample collected',
  dispatch: 'Sample dispatched',
  sample_sent: 'Sent to reference lab',
  reference_lab_changed: 'Reference lab changed',
  awaiting_report: 'Awaiting report',
  report_received: 'Report received',
  tracking_updated: 'Tracking updated',
  report_uploaded: 'Report uploaded',
  report_replaced: 'Report replaced',
  report_viewed: 'Report viewed',
  ocr_processed: 'OCR processed',
  result_entered: 'Result entered',
  result_edited: 'Result edited',
  outsourced_result_entry: 'Result saved',
  verified: 'Verified',
  sent_back: 'Sent back',
  result_verified: 'Result verified',
  result_sent_back: 'Result sent back',
  report_finalized: 'Report finalized',
  reopen_for_amendment: 'Reopened for correction',
  reopened_for_correction: 'Reopened for correction',
};

/** The audit trail, read back — the same rows audit_log holds, nothing reconstructed. */
function Timeline({ item }: { item: OutsourcedDetail }) {
  const [showViews, setShowViews] = useState(false);
  // Several actions write both a test-level and a result-level row; show the
  // test-level one so each event reads once.
  const entries = item.timeline.filter((t) => {
    const a = t.details?.action;
    if (!a) return false;
    if (t.table_name === 'lab_results' && ['verified', 'sent_back', 'reopen_for_amendment', 'outsourced_result_entry'].includes(a)) return false;
    if (a === 'report_viewed' && !showViews) return false;
    return true;
  });
  return (
    <section>
      <SectionTitle right={
        <label className="flex items-center gap-1 text-[11px] text-muted-foreground">
          <input type="checkbox" checked={showViews} onChange={(e) => setShowViews(e.target.checked)} /> include views
        </label>
      }>Audit trail</SectionTitle>
      <div className="divide-y divide-border rounded-xl border border-border text-[12px]">
        {entries.length === 0 && <div className="px-3 py-2 text-muted-foreground">Nothing recorded yet.</div>}
        {entries.map((t) => {
          const d = t.details ?? {};
          const extra = [d.reason, d.external_accession_number, d.reference_lab, d.original_name, d.comments]
            .filter((x) => typeof x === 'string' && x).join(' · ');
          return (
            <div key={t.id} className="flex flex-wrap justify-between gap-2 px-3 py-1.5">
              <span>
                <span className="font-medium">{ACTION_LABELS[String(d.action)] ?? String(d.action)}</span>
                {extra && <span className="text-muted-foreground"> — {extra}</span>}
              </span>
              <span className="text-muted-foreground">{t.changed_by ?? '—'} · {fmtDateTime(t.changed_at)}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}
